import { create } from 'zustand';
import { listPersonas, type PersonaInfo } from '@/service/persona';
import { EVENT } from '@/utils/event';
import { readActivePersonaId, writeActivePersonaId } from '@/utils/personaStorage';

interface PersonaState {
  personas: PersonaInfo[];
  activePersonaId: string | null;
  defaultPersonaId: string | null;
  loading: boolean;
  fetchPersonas: () => Promise<void>;
  switchPersona: (personaId: string) => void;
  getActivePersona: () => PersonaInfo | null;
}

export const usePersonaStore = create<PersonaState>((set, get) => ({
  personas: [],
  activePersonaId: null,
  defaultPersonaId: null,
  loading: false,

  fetchPersonas: async () => {
    set({ loading: true });
    try {
      const res = await listPersonas();
      if (res.data.code !== 0 || !res.data.data) {
        return;
      }
      const { personas, default_persona_id } = res.data.data;
      const stored = readActivePersonaId();
      const validIds = new Set(personas.map((p) => p.id));
      let active = stored && validIds.has(stored) ? stored : default_persona_id;
      if (!validIds.has(active) && personas.length > 0) {
        active = personas[0].id;
      }
      if (active) {
        writeActivePersonaId(active);
      }
      set({
        personas,
        defaultPersonaId: default_persona_id,
        activePersonaId: active
      });
    } catch {
      // Backend restart returns 500. Leave the page usable instead of an unhandled overlay.
    } finally {
      set({ loading: false });
    }
  },

  switchPersona: (personaId: string) => {
    const id = personaId.trim().toLowerCase();
    writeActivePersonaId(id);
    set({ activePersonaId: id });
    dispatchEvent(new Event(EVENT.PERSONA_CHANGED));
  },

  getActivePersona: () => {
    const { personas, activePersonaId } = get();
    return personas.find((p) => p.id === activePersonaId) ?? null;
  }
}));

export function getActivePersonaId(): string {
  return readActivePersonaId() || usePersonaStore.getState().activePersonaId || 'jason';
}
